import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { Form, Modal } from "react-bootstrap";
import { toast } from "sonner";
import Select from "react-select";
import { customStyles } from "../games/customStyles";

import makeAnimated from "react-select/animated";
import LoadingSpinner from "../../Components/spinner";
import { NotificationValidations } from "./validationSchema";
import {
  createNewNotification,
  editNotification,
  fetchEmployeesListForNotifs,
  fetchUsersListForNotifs,
} from "../../services/axios/apiManager";
import { handleErrors } from "../../services/dataManager";
import { useNavigate } from "react-router-dom";
import {
  SwitchButtonLarge,
  SwitchButtonMedium,
} from "../../Components/switchButton";

const AddEditNotification = ({
  addEditModal,
  openAddEditModal,
  selectedNotification,
  setSelectedNotification,
  pagination,
  fetchNotifications,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [usersData, setUsersData] = useState([]);
  const animatedComponents = makeAnimated();

  const handleCloseAddEditModal = () => {
    setSelectedNotification({});
    setLoading(true);
    openAddEditModal(false);
  };

  const formik = useFormik({
    initialValues: {
      title: selectedNotification.title || "",
      message: selectedNotification.message || "",
      type: selectedNotification.type || "general",
      target_type: selectedNotification.target_type || "users",
      target_ids: selectedNotification.target_ids || [],
      status: selectedNotification.status || "draft",
      priority: selectedNotification.priority || "low",
      platform: selectedNotification.platform || "web",
      isLive: false,
    },
    validationSchema: NotificationValidations,
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      
      const data = values;
      if (data.type === "targeted") {
        if (!data?.target_ids?.length) {
          toast.error("Select targeted persons", { duration: 2000 });
          return;
        }
      } else {
        delete data.target_ids;
      }
      if (selectedNotification?.id) {
        const res = await editNotification({
          id: selectedNotification.id,
          ...data,
        });
        if (res?.data?.success) {
          toast.success("Notification updated.", { duration: 2000 });
          resetForm();
          await fetchNotifications(pagination);
          handleCloseAddEditModal();
        } else {
          handleErrors(res, navigate, "New Notification:");
        }
      } else {
        const res = await createNewNotification(data);
        if (res?.data?.success) {
          toast.success("New notification Added.", { duration: 2000 });
          resetForm();
          await fetchNotifications(pagination);
          handleCloseAddEditModal();
        } else {
          handleErrors(res, navigate, "New Notification:");
        }
      }
    },
  });

  const handleChangeLive = async () => {
    const currentValue = formik?.values?.isLive;
    formik.setFieldValue("isLive", !currentValue);
  };

  // // // handle change type logic
  const handleChangeType = async (e) => {
    e.preventDefault();
    const str = e.target.value;
    formik.setFieldValue("type", str);
    if (str === "targeted" && formik?.values?.target_type === "employees") {
      await getEmployeesList();
    } else {
      formik.setFieldValue("target_ids", []);
    }
  };
  // // // handle Change Target Type
  const handleChangeTargetType = async (e) => {
    e.preventDefault();
    const str = e.target.value;
    formik.setFieldValue("target_type", str);
    formik.setFieldValue("target_ids", []);
    formik.setFieldValue("platform", "web");
    if (str === "users") {
      await getUsersList();
    } else {
      await getEmployeesList();
    }
  };

  // // // handle Get users list
  const getUsersList = async () => {
    const res = await fetchUsersListForNotifs();
    if (res?.data?.success && res?.data?.data) {
      const temp = res?.data?.data.map((m) => ({
        value: m.id,
        label: `${m.first_name || ""}${m.last_name || ""}`,
      }));
      setUsersData(temp);
    } else {
      toast.error("Error fetching users, try again later", { duration: 2000 });
    }
    setLoading(false);
  };

  // // // handle Get Employees list
  const getEmployeesList = async () => {
    const res = await fetchEmployeesListForNotifs();
    if (res?.data?.success && res?.data?.data) {
      const temp = res?.data?.data.map((m) => ({
        value: m.id,
        label: `${m.first_name || ""}${m.last_name || ""}`,
      }));
      setUsersData(temp);
    } else {
      toast.error("Error fetching users, try again later", { duration: 2000 });
    }
    setLoading(false);
  };

  useEffect(() => {
    if (addEditModal) {
      if (
        selectedNotification?.id &&
        selectedNotification?.target_type === "employees"
      ) {
        getEmployeesList();
      } else {
        getUsersList();
      }
    }
  }, [addEditModal]);
  return (
    <Modal
      show={addEditModal}
      onHide={handleCloseAddEditModal}
      centered
      className="text-light"
      backdrop="static"
      size="lg"
    >
      <Modal.Header closeButton className="bg-dark text-light border-0">
        <Modal.Title>
          {selectedNotification?.id
            ? "Edit Notification"
            : "Add New Notification"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-dark text-light">
        {loading ? (
          <div className="d-flex justify-content-center align-items-center h-96">
            <LoadingSpinner size={"lg"} />
          </div>
        ) : (
          <div>
            <div className="row">
              {/* Title */}
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <label className="form-label">Title</label>
                  <Form.Control
                    type="text"
                    data-testid="not-title"
                    placeholder="Enter Title"
                    className="bg-dark text-light border-light input-placeholder-light form-control"
                    {...formik.getFieldProps("title")}
                    isInvalid={formik.touched.title && !!formik.errors.title}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.title}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
              {/* Priority */}
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <label className="form-label">Priority</label>
                  <Form.Select
                    type="text"
                    className="bg-dark text-light border-light input-placeholder-light form-control"
                    {...formik.getFieldProps("priority")}
                    isInvalid={
                      formik.touched.priority && !!formik.errors.priority
                    }
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </Form.Select>
                  <Form.Control.Feedback priority="invalid">
                    {formik.errors.priority}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>

              {/* Type */}
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <label className="form-label">Type</label>
                  <Form.Select
                    type="text"
                    data-testid="notif-type"
                    className="bg-dark text-light border-light input-placeholder-light form-control"
                    value={formik.values.type}
                    onChange={(e) => handleChangeType(e)}
                    isInvalid={formik.touched.type && !!formik.errors.type}
                  >
                    <option value="general">General</option>
                    <option value="targeted">Targeted</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.type}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
              {/* Target_Type */}
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <label className="form-label">Target Type</label>
                  <Form.Select
                    type="text"
                    data-testid="notif-target-type"
                    className="bg-dark text-light border-light input-placeholder-light form-control"
                    value={formik.values.target_type}
                    onChange={(e) => handleChangeTargetType(e)}
                    isInvalid={
                      formik.touched.target_type && !!formik.errors.target_type
                    }
                  >
                    <option value="users">Users</option>
                    <option value="employees">Employees</option>
                  </Form.Select>
                  <Form.Control.Feedback target_type="invalid">
                    {formik.errors.target_type}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
              {formik?.values?.type === "targeted" && (
                <div className="col-lg-12">
                  <Form.Group className="mb-3">
                    <label htmlFor="ageGroupInput" className="form-label">
                      Targeted Persons
                    </label>
                    <Select
                      closeMenuOnSelect={false}
                      components={animatedComponents}
                      isSearchable={false}
                      isMulti
                      options={usersData}
                      styles={customStyles}
                      value={
                        usersData?.filter((option) =>
                          formik.values.target_ids?.includes(option.value)
                        ) || []
                      }
                      onChange={(selectedOptions) => {
                        formik.setFieldValue(
                          "target_ids",
                          selectedOptions
                            ? selectedOptions.map((option) => option.value)
                            : []
                        );
                      }}
                    />
                    {formik.errors?.target_ids && formik.touched?.target_ids ? (
                      <span className="text-danger">
                        {formik.errors?.target_ids}
                      </span>
                    ) : null}
                  </Form.Group>
                </div>
              )}
              {/* Status */}
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <label className="form-label">Status</label>
                  <Form.Select
                    type="text"
                    className="bg-dark text-light border-light input-placeholder-light form-control"
                    {...formik.getFieldProps("status")}
                    isInvalid={formik.touched.status && !!formik.errors.status}
                  >
                    <option value="draft">Draft</option>
                    <option value="sent">Sent</option>
                  </Form.Select>
                  <Form.Control.Feedback status="invalid">
                    {formik.errors.status}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>

              {/* Platform */}
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <label className="form-label">Platform</label>
                  <Form.Select
                    type="text"
                    className="bg-dark text-light border-light input-placeholder-light form-control"
                    {...formik.getFieldProps("platform")}
                    isInvalid={
                      formik.touched.platform && !!formik.errors.platform
                    }
                  >
                    <option value="web">Web</option>
                    {formik?.values?.target_type === "users" && (
                      <option value="both">Both</option>
                    )}
                    {formik?.values?.target_type === "users" && (
                      <option value="mobile">Mobile</option>
                    )}
                  </Form.Select>
                  <Form.Control.Feedback platform="invalid">
                    {formik.errors.platform}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
              {/* Message */}
              <div className="col-lg-12">
                <Form.Group className="mb-3">
                  <label className="form-label">Message</label>
                  <Form.Control
                    as="textarea"
                    data-testid="not-message"
                    rows={3}
                    className="bg-dark text-light border-light input-placeholder-light form-control"
                    {...formik.getFieldProps("message")}
                    isInvalid={
                      formik.touched.message && !!formik.errors.message
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
              {/* {!selectedNotification?.id && ( */}
                <div className="container-fluid my-2">
                  <div className="d-flex">
                    <SwitchButtonMedium
                      isOn={formik?.values?.isLive}
                      onClick={handleChangeLive}
                      color={"#00af43ff"}
                    />
                    <span className="mx-2">Send Push Notification</span>
                  </div>
                </div>
              {/* )} */}
            </div>
            <div className="container-fluid my-2">
              <div className="d-flex justify-content-end">
                {loading && <LoadingSpinner size={"lg"} />}
                <button
                  className="btn btn-secondary d-flex align-items-center mx-2"
                  onClick={() => {
                    handleCloseAddEditModal();
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-success d-flex align-items-center gap-2"
                  type="button"
                  onClick={() => {
                    formik.handleSubmit();
                  }}
                >
                  {selectedNotification?.id
                    ? "Update Notification"
                    : "Add Notification"}
                </button>
              </div>
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};
export default AddEditNotification;
